import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { PRE_STATUS, PrerequestBucket, PrerequestDTO, PrerequestListDTO, PrerequestPermissions, PrerequestPersonDTO, PrerequestStage } from "../../../../services/v2/enrollmentPrerequests.service";
import { EnrollmentRequestDetailsDTO, REQUEST_DOCUMENT_TYPE, REQUEST_MODE } from "../../../../models/EnrollmentHistoryDTO";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { horarioBonito } from "../../../../utils/horarioBonito";
import { telefonoBonito } from "../../../../utils/telefonoBonito";
import useEsTelefono from "../../../../utils/esTelefono";
import useEnlaceDirecto from "../../../../utils/enlaceDirecto";
import { horaDoce } from "../../../../components/enrollment/format";
import { abrirArchivo } from "../../../../components/enrollment/visorArchivo";
import { errorText } from "../../managment/enrollmentRequests/helpers";
import RequestFormModal, { RequestPrefill } from "../../managment/enrollmentRequests/requestFormModal";
import PreSolicitudForm from "./preSolicitudForm";
import ConfirmarPlantel, { PlantelElegido } from "../../managment/enrollmentRequests/confirmarPlantel";
import "./preSolicitudes.css";

type Bandeja = "proceso" | "matriculadas" | "descartadas";
const ESTADO_DE: Record<Bandeja, PrerequestBucket> = { proceso: "proceso", matriculadas: "matriculadas", descartadas: PRE_STATUS.DESCARTADA as 3 };
const POR_PAGINA = 100;   // 100 por pantalla, como el resto de Matrículas (Esteban, 2026-10-20); el servidor no da más de 100.

/*
	El camino completo de cada cliente, de la Solicitud a Matriculado (Esteban, 2026-10-19: "que las vendedoras puedan
	ver el full path de sus matrículas"). Verde solo al final; lo que se atoró (rechazada, descartada, error) en oro.
*/
const PASOS = ["Solicitud", "Matrícula", "Revisión", "Pago", "Matriculado"];
const PASO_DE: Record<PrerequestStage, number> = { solicitud: 0, matricula: 1, rechazada: 1, revision: 2, pago: 3, matriculado: 4, descartada: 0, error: 2 };
const ETIQUETA_DE: Record<PrerequestStage, string> = {
	solicitud: "Por completar", matricula: "Matrícula en borrador", rechazada: "Rechazada, por corregir", revision: "En revisión de Admisiones",
	pago: "Pendiente de pago en Caja", matriculado: "Matriculado", descartada: "Descartada", error: "Marcada como error"
};
const etapaDe = (pre: PrerequestDTO, p: PrerequestPersonDTO): PrerequestStage =>
	p.stage || (p.requestId ? "revision" : pre.statusId === PRE_STATUS.DESCARTADA ? "descartada" : "solicitud");

/** Lo que pasa con el formulario del cliente, en una línea (y si va en oro, porque no se pudo mandar). */
const lineaFormulario = (p: PrerequestPersonDTO): { texto: string; oro?: boolean } | null => {
	const f = p.form;
	if (!f || !f.status || p.requestId) return null;
	switch (f.status) {
		case "completado": return { texto: "Ya llenó sus datos" + (f.error ? " · para enviarla falta " + f.error.replace(/^Datos listos; falta completar: /, "").replace(/Falta: /g, "") : ""), oro: !!f.error };
		case "abierto": return { texto: "El cliente abrió el formulario" + (f.openedAt ? ", " + cuando(f.openedAt) : "") };
		case "enviado": return { texto: "Formulario enviado por WhatsApp" + (f.sentAt ? ", " + cuando(f.sentAt) : "") };
		case "apagado": return null;
		default: return { texto: "No se envió el formulario: " + (f.error || "intenta de nuevo"), oro: true };
	}
};

const Avance: React.FC<{ etapa: PrerequestStage; persona?: PrerequestPersonDTO }> = ({ etapa, persona }) => {
	const paso = PASO_DE[etapa];
	const atorada = etapa === "rechazada" || etapa === "descartada" || etapa === "error";
	const fin = etapa === "matriculado";
	return (
		<div className={"psAvance" + (fin ? " fin" : "") + (atorada ? " atorada" : "")} title={PASOS.map((t, i) => (i <= paso ? "● " : "○ ") + t).join("  ")}>
			<div className="psPasos" aria-hidden="true">
				{PASOS.map((t, i) => (
					<React.Fragment key={t}>
						{i > 0 ? <span className={"psRaya" + (i <= paso && !(atorada && i === paso) ? " hecha" : "")} /> : null}
						<span className={"psPunto" + (i < paso || fin ? " hecho" : i === paso ? " actual" : "")} />
					</React.Fragment>
				))}
			</div>
			<div className="psAvanceTexto">{etapa === "solicitud" && persona?.form?.status === "completado" ? "Datos listos, por completar"
				: etapa === "solicitud" && (persona?.form?.status === "enviado" || persona?.form?.status === "abierto") ? "Esperando al cliente" : ETIQUETA_DE[etapa]}</div>
			{persona && lineaFormulario(persona) ? (
				<div className={"psFormLinea" + (lineaFormulario(persona)?.oro ? " oro" : "")} title={lineaFormulario(persona)?.texto}>{lineaFormulario(persona)?.texto}</div>
			) : null}
			{/* Lo que le falta, completo: para llamarle y terminar con "Completar" (Esteban, 2026-10-19). */}
			{persona && !persona.requestId && (persona.form?.falta || []).length > 0 ? (
				<div className="psFalta" title={(persona.form?.falta || []).join(", ")}><b>Falta:</b> {(persona.form?.falta || []).join(", ")}</div>
			) : null}
		</div>
	);
};

/** Asesora en la lista: "Nilsy Magaly Guevara Rodriguez" → "Nilsy Guevara" (primer nombre + primer apellido). */
const nombreCorto = (n: string | null): string => {
	const partes = (n || "").trim().split(/\s+/).filter(Boolean);
	if (partes.length <= 2) return partes.join(" ");
	return partes[0] + " " + partes[partes.length >= 4 ? 2 : 1];
};

/** "hoy 3:12 pm", "ayer 9:05 am", "28/09 4:40 pm": siempre am/pm, nunca 24 h. */
const cuando = (local: string | null): string => {
	if (!local) return "";
	const [fecha, hora] = local.split("T");
	const hoy = new Date();
	const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
	const ayer = new Date(hoy.getTime() - 86400000);
	const [y, m, d] = fecha.split("-");
	const dia = fecha === iso(hoy) ? "hoy" : (fecha === iso(ayer) ? "ayer" : `${d}/${m}${y !== String(hoy.getFullYear()) ? "/" + y : ""}`);
	return `${dia} ${horaDoce(hora || "")}`.trim();
};

/**
 * Miniatura de un comprobante: las imágenes se ven chiquitas; todo se abre en el visor de WAPP.
 * Siempre con el plantel de la solicitud, no el de la barra: al cambiar de plantel (o al abrir un enlace de Cliq de otro
 * plantel) las tarjetas viejas seguían en pantalla y pedían su comprobante con el plantel nuevo → 404 (18-oct-2026).
 */
const Miniatura: React.FC<{ pre: PrerequestDTO; doc: { id: number; name: string; mimeType: string | null } }> = ({ pre, doc }) => {
	const campusId = pre.campusId;
	const http = HttpManager.getInstance();
	const [url, setUrl] = useState<string | null>(null);
	const esImagen = !!doc.mimeType && doc.mimeType.startsWith("image/") && !/hei[cf]/i.test(doc.mimeType);
	useEffect(() => {
		if (!esImagen) return;
		let vivo = true;
		let creada: string | null = null;
		http.enrollmentPrerequestsService.downloadDocument(campusId, pre.id, doc.id)
			.then((r) => { if (vivo) { creada = URL.createObjectURL(r.data); setUrl(creada); } })
			.catch(() => { /* queda el ícono */ });
		return () => { vivo = false; if (creada) URL.revokeObjectURL(creada); };
	}, [campusId, pre.id, doc.id]);
	return (
		<button type="button" className="psMiniatura" title={doc.name} aria-label={"Ver " + doc.name}
			onClick={() => abrirArchivo({ origen: "Solicitudes", nombre: doc.name, urlLista: url, cargar: () => http.enrollmentPrerequestsService.downloadDocument(campusId, pre.id, doc.id).then((r) => r.data) })}>
			{url ? <img src={url} alt="" /> : <FontAwesomeIcon icon={esImagen ? solid("image") : solid("file-pdf")} />}
		</button>
	);
};

/**
 * Solicitudes en Proceso: primera pestaña de Matrículas (Esteban, 2026-10-19; antes un menú aparte, 2026-10-03). La vendedora
 * crea Solicitudes y aquí sigue a cada cliente hasta que queda matriculado; la supervisora completa cada cliente en el
 * formulario de Matrícula de siempre o descarta con motivo. Una fila por cliente, agrupada por Solicitud.
 * El botón Nueva y la búsqueda viven en la cabeza de Matrículas (nuevaTick, busqueda).
 */
interface Props {
	busqueda?: string;
	nuevaTick?: number;
	onInfo?: (info: { permisos: PrerequestPermissions; cuentas: PrerequestListDTO["counts"] }) => void;
	onAbrirMatricula?: (requestId: number) => void;
	/** Quien completa es de Admisiones: lo que ella llena se matricula al enviarlo, no va a revisión (igual que el servidor). */
	esAdmisiones?: boolean;
}

const PreSolicitudesController: React.FC<Props> = ({ busqueda = "", nuevaTick = 0, onInfo, onAbrirMatricula, esAdmisiones = false }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	// Mismo plantel que la barra, como Matrículas (el de la sesión es solo respaldo).
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const currentCampus: any = legacyCampus
		? { id: legacyCampus.id, countryId: legacyCampus.company?.country_id ?? (sessionCampus?.id === legacyCampus.id ? sessionCampus?.countryId : undefined) }
		: sessionCampus;
	const campusId: number | null = currentCampus?.id ?? null;
	const countryId: number = Number(currentCampus?.countryId || 0);
	// "Inscribir" de la ficha del CRM (Esteban, 2026-10-12): abre Nueva Solicitud con nombre, teléfono, plantel y curso.
	const location = useLocation();
	const navigate = useNavigate();
	const [desdeFicha, setDesdeFicha] = useState<{ phone: string; name: string; curso: string; plantel: number | null } | null>(null);
	useEffect(() => {
		const n = (location.state as any)?.nuevaSolicitud;
		if (!n) return;
		const mios: any[] = legacyCampusRef ? Object.values(legacyCampusRef) : [];
		const plantel = n.plantel && mios.some((c: any) => Number(c?.id) === Number(n.plantel)) ? Number(n.plantel) : null;
		setDesdeFicha({ phone: String(n.phone || ""), name: String(n.name || ""), curso: String(n.curso || ""), plantel });
		setPedirPlantel(plantel || campusId || 0);
		navigate(location.pathname + location.search, { replace: true, state: null });
	}, [location.state]);
	// Plantel confirmado a propósito antes de cada Solicitud nueva (Esteban, 2026-10-12): sugerido = el de la ficha o el de la barra.
	const [pedirPlantel, setPedirPlantel] = useState<number | null>(null);
	const [nuevaEn, setNuevaEn] = useState<PlantelElegido | null>(null);
	const formCampus: number | null = nuevaEn?.id || desdeFicha?.plantel || campusId;
	const formPais: number = nuevaEn ? (nuevaEn.countryId || countryId) : desdeFicha?.plantel && desdeFicha.plantel !== campusId
		? Number((Object.values(legacyCampusRef || {}) as any[]).find((c: any) => Number(c?.id) === desdeFicha.plantel)?.company?.country_id || countryId) : countryId;
	// El botón Nueva de Matrículas: cada clic sube el contador (el 0 inicial no abre nada).
	const ultimaNueva = useRef<number>(nuevaTick);
	useEffect(() => {
		if (nuevaTick !== ultimaNueva.current) {
			ultimaNueva.current = nuevaTick;
			setPedirPlantel(campusId || 0);
		}
	}, [nuevaTick]);

	const [bandeja, setBandeja] = useState<Bandeja>("proceso");
	const [pagina, setPagina] = useState<number>(1);
	const [resultado, setResultado] = useState<PrerequestListDTO | null>(null);
	const [cargando, setCargando] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [tick, setTick] = useState<number>(0);
	const [nueva, setNueva] = useState<boolean>(false);
	/*
		Completar. Con varios clientes en la Solicitud van en grupo (2026-10-27): al terminar uno se sigue con el siguiente en la misma
		ventana, junto con el anterior (`junto`), y la última envía a todas. `hechos`: los ya guardados en esta pasada.
	*/
	const [completar, setCompletar] = useState<{ pre: PrerequestDTO; persona: PrerequestPersonDTO; junto?: { requestId: number; name: string }; hechos?: number[] } | null>(null);
	const [descartar, setDescartar] = useState<PrerequestDTO | null>(null);
	const [motivo, setMotivo] = useState<string>("");
	const [ocupado, setOcupado] = useState<boolean>(false);
	const [resaltada, setResaltada] = useState<number | null>(null);
	const [enlazada, setEnlazada] = useState<PrerequestDTO | null>(null);
	const [enviando, setEnviando] = useState<number | null>(null);

	// Enlace del aviso de Cliq: /enrollmentPrerequests?pre=12&plantel=4 abre esa solicitud arriba.
	const enlace = useEnlaceDirecto("pre", (id) => {
		setResaltada(id);
		if (campusId) {
			http.enrollmentPrerequestsService.details(campusId, id).then((r) => setEnlazada(r.data)).catch((e) => setError(errorText(e)));
		}
	});

	// Al cambiar de plantel no dejar en pantalla filas del anterior: sus botones (Completar, Descartar) irían al plantel nuevo.
	useEffect(() => {
		setResultado(null);
		setEnlazada((e) => (e && e.campusId === campusId ? e : null));
	}, [campusId]);

	useEffect(() => { setPagina(1); }, [busqueda]);

	useEffect(() => {
		if (campusId === null) return;
		let vigente = true;
		setCargando(true);
		setError(null);
		http.enrollmentPrerequestsService.list(campusId, ESTADO_DE[bandeja], busqueda, POR_PAGINA, pagina)
			.then((r) => { if (vigente) { setResultado(r.data); onInfo?.({ permisos: r.data.permissions, cuentas: r.data.counts }); } })
			.catch((e) => { if (vigente) setError(errorText(e)); })
			.finally(() => { if (vigente) setCargando(false); });
		return () => { vigente = false; };
	}, [campusId, bandeja, busqueda, pagina, tick]);

	const refrescar = () => setTick((t) => t + 1);
	const permisos = resultado?.permissions;
	const cuentas = resultado?.counts;
	const gestiona = !!permisos?.canManage;

	const comprobantesDe = (pre: PrerequestDTO, persona: PrerequestPersonDTO) => pre.documents.filter((d) => d.personId == null || d.personId === persona.id);
	const prefillDe = (pre: PrerequestDTO, persona: PrerequestPersonDTO): RequestPrefill => ({
		prerequestId: pre.id, personId: persona.id, phone: persona.phone, name: persona.name,
		scheduleIds: (persona.schedules || []).map((h) => h.scheduleId),
		advisors: (pre.advisors || []).map((a) => ({ userId: String(a.userId), name: a.name, sharePercentage: null })),
		notes: pre.notes, studentData: persona.studentData && Object.keys(persona.studentData).length > 0 ? persona.studentData : null,
		documentos: [
			// Los de toda la venta y el que pagó este cliente (el de otro cliente va a su propia matrícula).
			...comprobantesDe(pre, persona).map((d) => ({ id: d.id, name: d.name, typeId: REQUEST_DOCUMENT_TYPE.COMPROBANTE })),
			// La identificación del tutor que subió el cliente en su formulario entra como del tutor.
			...(persona.documents || []).map((d) => ({ id: d.id, name: d.name, typeId: d.typeId === REQUEST_DOCUMENT_TYPE.ID_TUTOR ? REQUEST_DOCUMENT_TYPE.ID_TUTOR : REQUEST_DOCUMENT_TYPE.ID_ALUMNO }))
		],
		verDocumento: (docId: number) => abrirArchivo({ origen: "Solicitudes",
			nombre: [...pre.documents, ...(persona.documents || [])].find((d) => d.id === docId)?.name || "Documento",
			cargar: () => http.enrollmentPrerequestsService.downloadDocument(pre.campusId, pre.id, docId).then((r) => r.data)
		})
	});

	const nombreCliente = (pre: PrerequestDTO, p: PrerequestPersonDTO) => {
		const n = (p.name || [p.studentData?.firstName, p.studentData?.lastName].filter(Boolean).join(" ") || "").trim();
		return n || "Cliente #" + (pre.people.findIndex((x) => x.id === p.id) + 1);
	};
	const pendientesDe = (c: { pre: PrerequestDTO; persona: PrerequestPersonDTO; hechos?: number[] }) => c.pre.people
		.filter((x) => x.id !== c.persona.id && !x.requestId && !(c.hechos || []).includes(x.id))
		.map((x) => ({ personId: x.id, nombre: nombreCliente(c.pre, x) }));

	const alGuardarSolicitud = (d: EnrollmentRequestDetailsDTO, accion: string) => {
		const pre = completar?.pre;
		setCompletar(null);
		setAviso(`Matrícula #${d.id} ${accion === "submitted" ? "enviada a revisión" : accion === "approved" ? "aprobada" : "guardada"}${pre ? ` desde la solicitud #${pre.id}` : ""}.`);
		setEnlazada(null);
		refrescar();
	};

	const confirmarDescarte = async () => {
		if (!descartar) return;
		if (motivo.trim().length < 5) {
			setError("Escribe el motivo.");
			return;
		}
		setOcupado(true);
		try {
			await http.enrollmentPrerequestsService.discard(descartar.campusId, descartar.id, motivo.trim());
			setAviso(`Solicitud #${descartar.id} descartada.`);
			setDescartar(null);
			setMotivo("");
			setEnlazada(null);
			refrescar();
		} catch (e) {
			setError(errorText(e));
		} finally {
			setOcupado(false);
		}
	};

	if (campusId === null) {
		return <React.Fragment />;
	}

	const lista = resultado?.data || [];
	const todas = [...(enlazada && !lista.some((p) => p.id === enlazada.id) ? [enlazada] : []), ...lista];

	const enviarFormulario = async (pre: PrerequestDTO, p: PrerequestPersonDTO) => {
		setEnviando(p.id);
		setError(null);
		try {
			const r = await http.enrollmentPrerequestsService.enviarFormulario(pre.campusId, pre.id, p.id);
			const f = r.data.people.find((x) => x.id === p.id)?.form;
			if (f?.status === "enviado") setAviso("Formulario enviado por WhatsApp a " + telefonoBonito(p.phone, countryId) + ".");
			else setError("No se envió: " + (f?.error || "intenta de nuevo") + " Copia el enlace y mándalo tú.");
			refrescar();
		} catch (e) {
			setError(errorText(e));
		} finally {
			setEnviando(null);
		}
	};
	const copiarEnlace = async (p: PrerequestPersonDTO) => {
		if (!p.form?.url) return;
		try {
			await navigator.clipboard.writeText(p.form.url);
			setAviso("Enlace copiado: pégalo en el chat del cliente.");
		} catch {
			window.prompt("Copia el enlace:", p.form.url);
		}
	};
	/** Enviar por WhatsApp / copiar el enlace del formulario: quien creó la Solicitud o su supervisora (el servidor manda url solo a ellas). */
	const accionesFormulario = (pre: PrerequestDTO, p: PrerequestPersonDTO) => !p.requestId && p.form?.url && pre.statusId === PRE_STATUS.PENDIENTE && p.form.status !== "completado" ? (
		<div className="psFormAcciones">
			<button type="button" disabled={enviando === p.id} onClick={(e) => { e.stopPropagation(); enviarFormulario(pre, p); }}>
				<FontAwesomeIcon icon={solid("paper-plane")} />{enviando === p.id ? "Enviando…" : p.form.status === "enviado" || p.form.status === "abierto" ? "Reenviar" : "Enviar Formulario"}
			</button>
			<button type="button" onClick={(e) => { e.stopPropagation(); copiarEnlace(p); }}><FontAwesomeIcon icon={solid("link")} />Copiar Enlace</button>
		</div>
	) : null;

	/** Completar (supervisora, Admisiones o la asesora de Admisiones que la creó; cliente sin matrícula) o el número de su matrícula, que abre el detalle. */
	const accionPersona = (pre: PrerequestDTO, p: PrerequestPersonDTO) => p.requestId ? (
		<button type="button" className="psVerMatricula" onClick={(e) => { e.stopPropagation(); onAbrirMatricula?.(p.requestId as number); }}>
			Matrícula #{p.requestId}<FontAwesomeIcon icon={solid("chevron-right")} />
		</button>
	) : (pre.statusId === PRE_STATUS.PENDIENTE && (pre.canComplete ?? gestiona) ? (
		<button type="button" className="psCompletar" onClick={(e) => { e.stopPropagation(); setCompletar({ pre, persona: p }); }}>Completar</button>
	) : null);

	const cursos = (p: PrerequestPersonDTO) => (p.schedules || []).length < 1
		? <div className="psSinCurso">Sin curso elegido</div>
		: (p.schedules || []).map((h) => (
			<div key={"h_" + h.scheduleId} className="psCursoFila">
				<span className="psCursoNombre">{cursoCorto(h.courseName) || h.courseName}</span>
				<span className="psCursoHorario">{horarioBonito(h.scheduleName, { plural: true, compacto: true, separador: " " }) || h.scheduleName}</span>
			</div>
		));

	const avisosPersona = (p: PrerequestPersonDTO) => (
		<>
			{!p.requestId ? <span className={"psDatosEstado" + (p.complete ? " ok" : "")}>{p.complete ? "Datos completos" : "Faltan datos"}</span> : null}
			{(p.samePhone || []).length > 0 ? <div className="psRepetido">Mismo teléfono en {(p.samePhone || []).map((id) => "#" + id).join(", ")}</div> : null}
		</>
	);

	const extrasSolicitud = (pre: PrerequestDTO) => (
		<>
			{pre.documents.length > 0 ? (
				<div className="psMinis chicas">{pre.documents.map((d) => {
					const de = d.personId != null ? pre.people.find((x) => x.id === d.personId) : undefined;
					// Con varios clientes, el comprobante dice de quién es ("Todos" = un depósito por toda la venta).
					const etiqueta = pre.people.length < 2 ? null : de ? nombreCliente(pre, de).split(/\s+/)[0] : "Todos";
					return etiqueta === null ? <Miniatura key={"d_" + d.id} pre={pre} doc={d} />
						: <span key={"d_" + d.id} className="psMiniDe"><Miniatura pre={pre} doc={d} /><small>{etiqueta}</small></span>;
				})}</div>
			) : null}
			{pre.notes ? <div className="psNotaCorta" title={pre.notes}>{pre.notes}</div> : null}
			{pre.statusId === PRE_STATUS.DESCARTADA ? (
				<div className="psMotivoCorto" title={pre.discardReason || ""}><b>Descartada</b>{pre.discardedBy ? ` por ${nombreCorto(pre.discardedBy)}` : ""}: {pre.discardReason}</div>
			) : null}
			{pre.statusId === PRE_STATUS.PENDIENTE && gestiona ? (
				<button type="button" className="psDescartarTexto" onClick={(e) => { e.stopPropagation(); setMotivo(""); setDescartar(pre); }}>Descartar</button>
			) : null}
		</>
	);

	const tabla = (
		<table className="table psTabla">
			{/* Porcentajes: con el menú fijo la tabla mide ~900 px y 906 px fijos dejaban la última columna en 0 (Esteban, 2026-10-22). */}
			<colgroup>
				<col style={{ width: "11%" }} /><col style={{ width: "20%" }} /><col style={{ width: "20%" }} />
				<col style={{ width: "17%" }} /><col style={{ width: "14%" }} /><col style={{ width: "18%" }} />
			</colgroup>
			<thead>
				<tr><th>Solicitud</th><th>Cliente</th><th>Curso / Horario</th><th>Avance</th><th /><th>Comprobantes y Notas</th></tr>
			</thead>
			<tbody>
				{todas.map((pre) => pre.people.map((p, i) => {
					const etapa = etapaDe(pre, p);
					return (
						<tr key={"p_" + p.id} className={(i === 0 ? "psInicio" : "") + (pre.people.length > 1 ? " psGrupo" : "") + (resaltada === pre.id ? " resaltada" : "") + (p.requestId ? " clic" : "")}
							onClick={p.requestId ? () => onAbrirMatricula?.(p.requestId as number) : undefined} style={{ opacity: cargando ? 0.6 : 1 }}>
							{i === 0 ? (
								<td rowSpan={pre.people.length} className="psCeldaSolicitud">
									<b>#{pre.id}</b>
									{pre.people.length > 1 ? <div><span className="psJuntos"><FontAwesomeIcon icon={solid("users")} />{pre.people.length} juntos</span></div> : null}
									<div className="psSub" title={pre.createdBy || ""}>{nombreCorto(pre.createdBy) || "-"}</div>
									<div className="psSub">{cuando(pre.createdAt)}</div>
								</td>
							) : null}
							<td>
								<div className="psNombreCliente">{p.name ? <span title={p.name}>{p.name}</span> : <span className="psSinNombre">Cliente {i + 1}</span>}</div>
								<div className="psSub">{telefonoBonito(p.phone, countryId)}</div>
								{avisosPersona(p)}
							</td>
							<td>{cursos(p)}</td>
							<td><Avance etapa={etapa} persona={p} /></td>
							<td className="psCeldaAccion">{accionPersona(pre, p)}{accionesFormulario(pre, p)}</td>
							{i === 0 ? <td rowSpan={pre.people.length} className="psCeldaExtras">{extrasSolicitud(pre)}</td> : null}
						</tr>
					);
				}))}
			</tbody>
		</table>
	);

	// En el celular, una tarjeta por Solicitud con sus clientes dentro (misma información que la tabla).
	const tarjetas = (
		<div className="psGrid" style={{ opacity: cargando ? 0.6 : 1 }}>
			{todas.map((pre) => (
				<div key={"pre_" + pre.id} className={"psCard" + (pre.people.length > 1 ? " psGrupo" : "") + (resaltada === pre.id ? " resaltada" : "")}>
					<div className="psCardCabeza">
						<div>
							<div className="psCardNum">Solicitud #{pre.id}{pre.people.length > 1 ? <span className="psJuntos"><FontAwesomeIcon icon={solid("users")} />{pre.people.length} juntos</span> : null}</div>
							<div className="psCardQuien">{nombreCorto(pre.createdBy) || "-"}, {cuando(pre.createdAt)}</div>
						</div>
					</div>
					{pre.people.map((p, i) => (
						<div key={"p_" + p.id} className="psPersona" onClick={p.requestId ? () => onAbrirMatricula?.(p.requestId as number) : undefined}>
							<div className="psPersonaDatos">
								<div className="psPersonaNombre">{p.name || "Cliente " + (i + 1)} <span className="psSub">· {telefonoBonito(p.phone, countryId)}</span></div>
								{cursos(p)}
								{avisosPersona(p)}
								<Avance etapa={etapaDe(pre, p)} persona={p} />
							</div>
							<div className="psCeldaAccion">{accionPersona(pre, p)}{accionesFormulario(pre, p)}</div>
						</div>
					))}
					<div className="psCardPieExtras">{extrasSolicitud(pre)}</div>
				</div>
			))}
		</div>
	);

	return (
		<div className="psLista embebida">
			{enlace.aviso ? <div className="alert alert-warning" role="alert" style={{ fontSize: 15, cursor: "pointer" }} onClick={enlace.cerrar}>{enlace.aviso}</div> : null}
			{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : null}
			{aviso ? (
				<div className="alert alert-success" style={{ fontSize: 15, display: "flex", justifyContent: "space-between", gap: 10 }}>
					{aviso}<button type="button" className="btn-close" style={{ fontSize: 10 }} onClick={() => setAviso(null)} />
				</div>
			) : null}
			<div className="psTabs" role="tablist">
				{([["proceso", "En Proceso", cuentas?.inProcess], ["matriculadas", "Matriculadas", cuentas?.enrolled], ["descartadas", "Descartadas", cuentas?.discarded]] as [Bandeja, string, number | undefined][]).map(([k, t, n]) => (
					<button type="button" role="tab" key={k} aria-selected={bandeja === k} className={"psTab" + (bandeja === k ? " activa" : "")}
						onClick={() => { setBandeja(k); setPagina(1); }}>
						{t}{n !== undefined ? <span className="psCuenta">{n}</span> : null}
					</button>
				))}
			</div>
			{cargando && !resultado ? <div className="loader" /> : (
				todas.length < 1 ? <div className="psVacio">{busqueda ? "Nada con esa búsqueda" : bandeja === "proceso" ? "No hay solicitudes en proceso" : "Sin solicitudes"}</div>
					: movil ? tarjetas : tabla
			)}
			{resultado && resultado.lastPage > 1 ? (
				<div className="psPaginas">
					<button type="button" className="psTab" aria-label="Página anterior" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}><FontAwesomeIcon icon={solid("arrow-left")} /></button>
					<span style={{ alignSelf: "center" }}>{resultado.currentPage} de {resultado.lastPage}</span>
					<button type="button" className="psTab" aria-label="Página siguiente" disabled={pagina >= resultado.lastPage} onClick={() => setPagina((p) => p + 1)}><FontAwesomeIcon icon={solid("arrow-right")} /></button>
				</div>
			) : null}

			{pedirPlantel !== null ? (
				<ConfirmarPlantel que="solicitud" sugerido={pedirPlantel || null} onCerrar={() => { setPedirPlantel(null); setDesdeFicha(null); }}
					onElegir={(p) => { setNuevaEn(p); setPedirPlantel(null); setNueva(true); }} />
			) : null}
			{nueva ? (
				<PreSolicitudForm campusId={formCampus as number} countryId={formPais} inicial={desdeFicha} plantelNombre={nuevaEn?.nombre}
					onClose={() => { setNueva(false); setDesdeFicha(null); setNuevaEn(null); }}
					onSent={() => { setBandeja("proceso"); setPagina(1); refrescar(); }} />
			) : null}

			{completar ? (
				<RequestFormModal key={"c" + completar.persona.id} campusId={campusId} countryId={countryId} requestId={null} initialModeId={REQUEST_MODE.REMOTA}
					canPresencial={false} esAdmisiones={esAdmisiones} prefill={prefillDe(completar.pre, completar.persona)} bundleWith={completar.junto || null}
					clientesSolicitud={pendientesDe(completar)}
					onCompletarOtro={(personId, junto) => {
						const persona = completar.pre.people.find((x) => x.id === personId);
						if (persona) setCompletar({ pre: completar.pre, persona, junto, hechos: [...(completar.hechos || []), completar.persona.id] });
						refrescar();
					}}
					onClose={() => { setCompletar(null); refrescar(); }} onSaved={alGuardarSolicitud} />
			) : null}

			<Modal show={descartar !== null} onHide={ocupado ? undefined : () => setDescartar(null)} centered fullscreen="sm-down" className="waModulo">
				<Modal.Header closeButton={!ocupado}>
					<Modal.Title style={{ fontSize: 18 }}>Descartar Solicitud #{descartar?.id}</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ textAlign: "left" }}>
					<label htmlFor="psMotivo" style={{ position: "static", display: "block", fontWeight: 700, marginBottom: 6 }}>Motivo</label>
					<textarea id="psMotivo" className="form-control" rows={3} value={motivo} autoFocus onChange={(e) => setMotivo(e.target.value)} />
				</Modal.Body>
				<Modal.Footer>
					<button type="button" className="btn btn-outline-secondary" disabled={ocupado} onClick={() => setDescartar(null)}>Volver</button>
					<button type="button" className="btn btn-primary" disabled={ocupado || motivo.trim().length < 5} onClick={confirmarDescarte}>Descartar</button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default PreSolicitudesController;
